import React from 'react'
import PropTypes from 'prop-types'
import { matchPath } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

export function AdminHero({ eyebrow, title, description, actions, stats = [] }) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        background:
          'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(25,118,210,0.02) 45%, rgba(15,23,42,0.02) 100%)',
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
        >
          <Stack spacing={1.25} sx={{ maxWidth: 840 }}>
            {eyebrow ? (
              <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1 }}>
                {eyebrow}
              </Typography>
            ) : null}
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            {description ? (
              <Typography variant="body1" color="text.secondary">
                {description}
              </Typography>
            ) : null}
          </Stack>

          {actions ? <Box sx={{ width: { xs: '100%', lg: 'auto' } }}>{actions}</Box> : null}
        </Stack>

        {stats.length > 0 ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 2.5, flexWrap: 'wrap', rowGap: 1 }}
          >
            {stats.map((stat) => (
              <Chip
                key={stat.label}
                label={`${stat.label}: ${stat.value}`}
                color={stat.color || 'default'}
                variant={stat.variant || 'outlined'}
              />
            ))}
          </Stack>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function AdminSectionCard({ title, subtitle, actions, children, sx }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, ...sx }}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ px: 2.5, py: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
              {subtitle ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            {actions ? <Box>{actions}</Box> : null}
          </Stack>
        </Box>
        <Divider />
        <Box sx={{ p: 2.5 }}>{children}</Box>
      </CardContent>
    </Card>
  )
}

export function AdminModuleCard({ icon, title, description, meta, primaryAction, secondaryAction }) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 3,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
          borderColor: 'primary.main',
        },
      }}
    >
      <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: 'action.hover',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          </Box>
        </Stack>

        {meta ? (
          <Typography variant="caption" color="text.secondary">
            {meta}
          </Typography>
        ) : null}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 'auto' }}>
          {primaryAction}
          {secondaryAction}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default function AdminConsoleShell({ navigation = [], currentPath, sidebarFooter, children }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '280px minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
      <Card variant="outlined" sx={{ borderRadius: 3, position: { lg: 'sticky' }, top: { lg: 88 } }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 2.5, py: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Administrative navigation
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Control Panel
            </Typography>
          </Box>
          <Divider />
          <List sx={{ py: 1 }}>
            {navigation.map((item) => {
              const active = Boolean(matchPath({ path: item.path, end: true }, currentPath || ''))
              return (
                <ListItemButton
                  key={item.label}
                  selected={active}
                  aria-current={active ? 'page' : undefined}
                  onClick={item.onClick}
                  sx={{
                    mx: 1,
                    my: 0.5,
                    borderRadius: 2,
                    alignItems: 'flex-start',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: active ? 'primary.main' : 'text.secondary', mt: 0.25 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    secondary={item.description}
                    primaryTypographyProps={{ fontWeight: active ? 700 : 600 }}
                    secondaryTypographyProps={{ sx: { mt: 0.35 } }}
                  />
                  <ChevronRightIcon fontSize="small" color={active ? 'primary' : 'disabled'} />
                </ListItemButton>
              )
            })}
          </List>
          {sidebarFooter ? (
            <>
              <Divider />
              <Box sx={{ p: 2.5 }}>{sidebarFooter}</Box>
            </>
          ) : null}
        </CardContent>
      </Card>

      <Stack spacing={3}>{children}</Stack>
    </Box>
  )
}

AdminConsoleShell.propTypes = {
  navigation: PropTypes.array,
  currentPath: PropTypes.string,
  sidebarFooter: PropTypes.node,
  children: PropTypes.node,
}

AdminHero.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  actions: PropTypes.node,
  stats: PropTypes.array,
}

AdminSectionCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  actions: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.object,
}

AdminModuleCard.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  meta: PropTypes.string,
  primaryAction: PropTypes.node,
  secondaryAction: PropTypes.node,
}
